import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import AdminLayout from '../components/AdminLayout';
import { 
  BarChart3, Users, Zap, Activity, 
  CheckCircle, AlertCircle, Target, Rocket, Shield
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../utils/api';

export default function AdminStats() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchStats();
  }, [isAuthenticated, user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getStats();
      setStats(response.data.data || {});
    } catch (error) {
      console.error('[AdminStats] Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
          <div className="text-[#A0AEC0] text-lg">{t('Loading statistics...')}</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#0B0E14] relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Background Gradient */}
        <div className="fixed inset-0 bg-gradient-to-br from-[#128C7E]/5 via-transparent to-[#128C7E]/3 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          
          {/* Header Section */}
          <div className="mb-8 sm:mb-10 lg:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-2 sm:mb-3">
              📊 {t('System Statistics')}
            </h1>
            <p className="text-[#A0AEC0] text-sm sm:text-lg">
              {t('Overview of system performance and usage')}
            </p>
          </div>

          {/* Premium KPI Stats Grid - 4 Columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8 sm:mb-10 lg:mb-12">
            
            {/* Total Users Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#128C7E]/20 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#128C7E]/30 group-hover:border-[#128C7E]/60 rounded-3xl p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#128C7E]/30">
                <div className="flex items-start justify-between mb-8">
                  <div className="flex-1">
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('Total Users')}</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#3B82F6]/30 to-[#3B82F6]/10 rounded-2xl flex items-center justify-center group-hover:from-[#3B82F6]/40 group-hover:to-[#3B82F6]/20 transition-all">
                    <Users className="w-7 h-7 text-[#3B82F6]" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-5xl font-black text-white">{stats?.totalUsers || '0'}</p>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2D3748]">
                    <span className="text-[#A0AEC0] text-xs">{t('Active accounts')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Plans Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#A855F7]/20 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#A855F7]/30 group-hover:border-[#A855F7]/60 rounded-3xl p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#A855F7]/30">
                <div className="flex items-start justify-between mb-8">
                  <div className="flex-1">
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('Total Plans')}</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#A855F7]/30 to-[#A855F7]/10 rounded-2xl flex items-center justify-center group-hover:from-[#A855F7]/40 group-hover:to-[#A855F7]/20 transition-all">
                    <BarChart3 className="w-7 h-7 text-[#A855F7]" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-5xl font-black text-white">{stats?.totalPlans || '0'}</p>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2D3748]">
                    <span className="text-[#A0AEC0] text-xs">{t('Billing plans')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total API Keys Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#EF4444]/20 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#EF4444]/30 group-hover:border-[#EF4444]/60 rounded-3xl p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#EF4444]/30">
                <div className="flex items-start justify-between mb-8">
                  <div className="flex-1">
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('Total API Keys')}</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#EF4444]/30 to-[#EF4444]/10 rounded-2xl flex items-center justify-center group-hover:from-[#EF4444]/40 group-hover:to-[#EF4444]/20 transition-all">
                    <Zap className="w-7 h-7 text-[#EF4444]" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-5xl font-black text-white">{stats?.totalApiKeys || '0'}</p>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2D3748]">
                    <span className="text-[#A0AEC0] text-xs">{t('Active keys')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* System Health Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#10B981]/20 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative h-full bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#10B981]/30 group-hover:border-[#10B981]/60 rounded-3xl p-8 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#10B981]/30">
                <div className="flex items-start justify-between mb-8">
                  <div className="flex-1">
                    <p className="text-[#A0AEC0] text-xs font-bold uppercase tracking-wider">{t('System Health')}</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#10B981]/30 to-[#10B981]/10 rounded-2xl flex items-center justify-center group-hover:from-[#10B981]/40 group-hover:to-[#10B981]/20 transition-all">
                    <Activity className="w-7 h-7 text-[#10B981]" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-5xl font-black text-white">{stats?.uptime_percentage || 99}%</p>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2D3748]">
                    <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></span>
                    <span className="text-[#A0AEC0] text-xs">{t('System running')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* System Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            
            {/* OTP Requests Card */}
            <div className="group">
              <div className="relative bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#2D3748] group-hover:border-[#128C7E]/40 rounded-3xl p-8 shadow-2xl transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-[#A0AEC0] text-sm font-bold uppercase tracking-wider">{t('Total OTP Requests')}</p>
                  </div>
                  <Rocket className="w-6 h-6 text-[#128C7E]" />
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-5xl font-black text-white">
                      {stats?.totalOtpRequests ? new Intl.NumberFormat(i18n.language, {notation: 'compact'}).format(stats.totalOtpRequests) : '0'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#A0AEC0] text-xs">{t('All time')}</p>
                    <p className="text-[#128C7E] text-sm font-bold">↑ 24%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Successful Deliveries Card */}
            <div className="group">
              <div className="relative bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#2D3748] group-hover:border-[#10B981]/40 rounded-3xl p-8 shadow-2xl transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-[#A0AEC0] text-sm font-bold uppercase tracking-wider">{t('Successful Deliveries')}</p>
                  </div>
                  <CheckCircle className="w-6 h-6 text-[#10B981]" />
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-5xl font-black text-white">
                      {stats?.successfulDeliveries ? new Intl.NumberFormat(i18n.language, {notation: 'compact'}).format(stats.successfulDeliveries) : '0'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#A0AEC0] text-xs">{t('Success rate')}</p>
                    <p className="text-[#10B981] text-sm font-bold">99.8%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Failed Deliveries Card */}
            <div className="group">
              <div className="relative bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#2D3748] group-hover:border-[#EF4444]/40 rounded-3xl p-8 shadow-2xl transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-[#A0AEC0] text-sm font-bold uppercase tracking-wider">{t('Failed Deliveries')}</p>
                  </div>
                  <AlertCircle className="w-6 h-6 text-[#EF4444]" />
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-5xl font-black text-[#EF4444]">
                      {stats?.failedDeliveries ? new Intl.NumberFormat(i18n.language, {notation: 'compact'}).format(stats?.failedDeliveries) : '0'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#A0AEC0] text-xs">{t('Failed')}</p>
                    <p className="text-[#EF4444] text-sm font-bold">0.2%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Insights & Actions Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Quick Insights */}
            <div className="group">
              <div className="relative bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#2D3748] group-hover:border-[#128C7E]/40 rounded-3xl p-8 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Target className="w-6 h-6 text-[#128C7E]" />
                  {t('System Performance')}
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-[#0B0E14]/50 border border-[#10B981]/20 rounded-2xl hover:border-[#10B981]/50 transition-all cursor-pointer">
                    <p className="text-white font-semibold text-sm">✓ {t('API Response Time')}</p>
                    <p className="text-[#A0AEC0] text-xs mt-1">Avg 45ms</p>
                  </div>

                  <div className="p-4 bg-[#0B0E14]/50 border border-[#10B981]/20 rounded-2xl hover:border-[#10B981]/50 transition-all cursor-pointer">
                    <p className="text-white font-semibold text-sm">✓ {t('Server Uptime')}</p>
                    <p className="text-[#A0AEC0] text-xs mt-1">99.99%</p>
                  </div>

                  <div className="p-4 bg-[#0B0E14]/50 border border-[#10B981]/20 rounded-2xl hover:border-[#10B981]/50 transition-all cursor-pointer">
                    <p className="text-white font-semibold text-sm">✓ {t('Database Performance')}</p>
                    <p className="text-[#A0AEC0] text-xs mt-1">Optimal</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="group">
              <div className="relative bg-gradient-to-br from-[#1A1F2E]/80 to-[#141820]/80 backdrop-blur-xl border border-[#2D3748] group-hover:border-[#128C7E]/40 rounded-3xl p-8 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-[#128C7E]" />
                  {t('System Security')}
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-[#0B0E14]/50 border border-[#10B981]/20 rounded-2xl hover:border-[#10B981]/50 transition-all cursor-pointer">
                    <p className="text-white font-semibold text-sm">🔒 {t('SSL Certificate')}</p>
                    <p className="text-[#A0AEC0] text-xs mt-1">{t('Valid')} - Expires in 365 days</p>
                  </div>

                  <div className="p-4 bg-[#0B0E14]/50 border border-[#10B981]/20 rounded-2xl hover:border-[#10B981]/50 transition-all cursor-pointer">
                    <p className="text-white font-semibold text-sm">🛡️ {t('API Authentication')}</p>
                    <p className="text-[#A0AEC0] text-xs mt-1">JWT Enabled</p>
                  </div>

                  <div className="p-4 bg-[#0B0E14]/50 border border-[#10B981]/20 rounded-2xl hover:border-[#10B981]/50 transition-all cursor-pointer">
                    <p className="text-white font-semibold text-sm">📋 {t('Rate Limiting')}</p>
                    <p className="text-[#A0AEC0] text-xs mt-1">Active</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </AdminLayout>
  );
}
